"use client";

import { useEffect, useState } from "react";
import NextImage from "next/image";

interface ImageProps {
    src: File | string | null;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    transition?: boolean;
    fill?: string;
    rounded?: boolean;
}

const imageLoader = ({
    src,
    width,
    quality,
}: {
    src: string;
    width: number;
    quality?: number;
}) => {
    if (src.startsWith("http")) {
        return src;
    } else {
        return `${process.env.NEXT_PUBLIC_WEBSITE_URL}/${src}?w=${width}&q=${
            quality || 75
        }`;
    }
};

export const Image = ({
    src,
    alt,
    width = 0,
    height = 0,
    className,
    transition = true,
    priority = false,
    rounded = false,
}: ImageProps) => {
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(false);
        const newImgSrc = src instanceof File ? URL.createObjectURL(src) : src;
        if (imgSrc !== newImgSrc) {
            setImgSrc(newImgSrc);
        }
        // We don't need to re-run this effect when the imgSrc changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    return (
        imgSrc && (
            <NextImage
                loader={imageLoader}
                src={imgSrc}
                alt={alt}
                width={width}
                height={height}
                priority={priority}
                className={className}
                style={{
                    width: width || "auto",
                    height: height || "auto",
                    opacity: isLoaded ? 1 : transition ? 0 : 1,
                    transition: transition
                        ? "opacity 0.4s ease-in-out"
                        : "none",
                    borderRadius: rounded ? "10px" : "0px",
                    borderTopRightRadius: rounded ? "4px" : "0px",
                }}
                onLoad={() => setIsLoaded(true)}
            />
        )
    );
};
